import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createDraft, deleteEntry, getEntry, saveEntry } from '../../db/entries';
import { listCategories } from '../../db/categories';
import { emptyProjects, getProjectsDoc, listProjects } from '../../db/projects';
import {
  deleteMedia,
  discardMediaForEntry,
  enqueueMediaUploads,
  findExifGps,
  putMedia,
  putStrokes,
} from '../../db/media';
import { ensureMediaBlob, ensureStrokes } from '../../sync/mediaFetch';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { newId } from '../../domain/ids';
import {
  SCHEMA_VERSION,
  type Category,
  type Entry,
  type MediaRef,
  type ProjectsDoc,
  type StrokeDoc,
} from '../../domain/types';
import {
  dateKeyToDate,
  fromDatetimeLocalValue,
  monthKeyOfIsoLocal,
  nowIsoUtc,
  toDatetimeLocalValue,
  toIsoLocal,
} from '../../lib/datetime';
import { DrawingCanvas } from '../components/DrawingCanvas';
import { processPhotos, type PhotoError } from '../../lib/photo';
import {
  exifLocation,
  formatLatLng,
  geolocationAvailable,
  getDeviceLocation,
  LocationError,
  mapUrl,
} from '../../lib/location';
import { useBreakpoint } from '../hooks/useBreakpoint';

const DRAFT_KEY = 'nj.draft';

interface EditorMedia {
  ref: MediaRef;
  /** 画像が端末に無い（未取得・オフライン）場合は null。参照自体は必ず保持する。 */
  url: string | null;
  strokes?: StrokeDoc;
}

function saveLocalDraft(entry: Entry) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(entry));
  } catch {
    /* 容量超過時は諦める */
  }
}
const clearLocalDraft = () => localStorage.removeItem(DRAFT_KEY);
function readLocalDraft(): Entry | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Entry) : null;
  } catch {
    return null;
  }
}

export function EntryEdit() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const version = useDataVersion();

  const isNew = !id;
  const [entry, setEntry] = useState<Entry | null>(null);
  const [media, setMedia] = useState<EditorMedia[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawing, setDrawing] = useState<{ mediaId: string | null; doc: StrokeDoc | null } | null>(null);
  const urlsRef = useRef<string[]>([]);
  const savedRef = useRef(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const bp = useBreakpoint();

  const categories = useAsync<Category[]>(() => listCategories(), [version], []);
  const projectsDoc = useAsync<ProjectsDoc>(getProjectsDoc, [version], emptyProjects());

  const trackUrl = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    urlsRef.current.push(url);
    return url;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let base: Entry | null = null;

      if (id) {
        base = (await getEntry(id)) ?? null;
      } else {
        const dateParam = params.get('date');
        const d = dateParam ? dateKeyToDate(dateParam) : new Date();
        if (dateParam) {
          const now = new Date();
          d.setHours(now.getHours(), now.getMinutes(), 0, 0);
        }
        const recovered = readLocalDraft();
        if (recovered && confirm('保存されていない下書きがあります。復元しますか？')) {
          base = recovered;
        } else {
          clearLocalDraft();
          base = createDraft(d);
          const pid = params.get('project');
          if (pid) base = { ...base, projectId: pid };
        }
      }
      if (!base || cancelled) {
        if (!cancelled) setEntry(base);
        return;
      }

      // 添付済みメディアを読み込む。
      // 端末にキャッシュが無ければ OneDrive から取りに行く。
      // 取れなかった場合でも参照は必ず残す。ここで落とすと、
      // 保存した時点で記事から添付が永久に外れてしまう。
      const month = monthKeyOfIsoLocal(base.date);
      const loaded: EditorMedia[] = [];
      for (const ref of base.media) {
        const blob = await ensureMediaBlob(ref, month, base.id);
        loaded.push({
          ref,
          url: blob ? trackUrl(blob) : null,
          strokes: await ensureStrokes(ref, month, base.id),
        });
      }
      if (cancelled) return;
      setEntry(base);
      setMedia(loaded);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // オブジェクトURLの後始末
  useEffect(
    () => () => {
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = [];
    },
    [],
  );

  const patch = useCallback((p: Partial<Entry>) => {
    setEntry((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...p };
      saveLocalDraft(next);
      return next;
    });
    setDirty(true);
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /** 写真の取り込み（仕様書 §9.2）。長辺1024pxへ縮小し、JPEG 0.85 で保存する。 */
  const addPhotos = async (files: File[]) => {
    if (!entry || files.length === 0) return;
    setImporting(true);
    setError(null);
    const failures: PhotoError[] = [];
    try {
      const month = monthKeyOfIsoLocal(entry.date);
      const processed = await processPhotos(files, (e) => failures.push(e));
      const added: EditorMedia[] = [];

      for (const p of processed) {
        const mediaId = newId();
        const ref: MediaRef = {
          id: mediaId,
          type: 'photo',
          file: `${mediaId}.jpg`,
          mime: 'image/jpeg',
          width: p.width,
          height: p.height,
          bytes: p.blob.size,
          addedAt: nowIsoUtc(),
        };
        await putMedia({
          id: mediaId,
          entryId: entry.id,
          month,
          file: ref.file,
          mime: ref.mime,
          blob: p.blob,
          bytes: p.blob.size,
          exifGps: p.gps,
          _uploaded: false,
        });
        added.push({ ref, url: trackUrl(p.blob) });
      }

      if (added.length > 0) {
        setMedia((prev) => [...prev, ...added]);
        setDirty(true);
      }
      if (failures.length > 0) {
        setError(failures.map((f) => `${f.fileName}: ${f.message}`).join('\n'));
      }
    } finally {
      setImporting(false);
    }
  };

  const pickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    void addPhotos(files);
  };

  // PC: クリップボードから貼り付け
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files ?? []).filter((f) =>
        f.type.startsWith('image/'),
      );
      if (files.length > 0) {
        e.preventDefault();
        void addPhotos(files);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  });

  /** 手書きの保存。新規は追加、既存は同じ media.id のまま差し替える（仕様書 §10.4）。 */
  const onDrawingDone = async (
    doc: StrokeDoc,
    png: Blob,
    size: { width: number; height: number },
  ) => {
    if (!entry) return;
    const month = monthKeyOfIsoLocal(entry.date);
    const editingId = drawing?.mediaId ?? null;
    const mediaId = editingId ?? newId();

    const ref: MediaRef = {
      id: mediaId,
      type: 'drawing',
      file: `${mediaId}.png`,
      mime: 'image/png',
      width: size.width,
      height: size.height,
      bytes: png.size,
      strokesFile: `${mediaId}.strokes.json`,
      addedAt: nowIsoUtc(),
    };

    await putMedia({
      id: mediaId,
      entryId: entry.id,
      month,
      file: ref.file,
      mime: ref.mime,
      blob: png,
      bytes: png.size,
      _uploaded: false,
    });
    await putStrokes({ id: mediaId, entryId: entry.id, month, data: doc, _uploaded: false });

    const url = trackUrl(png);
    setMedia((prev) => {
      const item: EditorMedia = { ref, url, strokes: doc };
      return editingId ? prev.map((m) => (m.ref.id === editingId ? item : m)) : [...prev, item];
    });
    setDirty(true);
    setDrawing(null);
  };

  const removeMedia = async (mediaId: string) => {
    if (!entry) return;
    if (!confirm('この画像を削除します。よろしいですか？')) return;
    await deleteMedia(mediaId, monthKeyOfIsoLocal(entry.date));
    setMedia((prev) => prev.filter((m) => m.ref.id !== mediaId));
    setDirty(true);
  };

  const moveMedia = (index: number, delta: number) => {
    setMedia((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  /**
   * 位置情報の取得（仕様書 §11.1）。
   * 添付写真のEXIFを優先し、無ければ端末の現在地を使う。自動取得はしない。
   */
  const captureLocation = async () => {
    if (!entry || locating) return;
    setLocating(true);
    setError(null);
    try {
      const gps = await findExifGps(media.map((m) => m.ref.id));
      patch({ location: gps ? exifLocation(gps) : await getDeviceLocation() });
    } catch (e) {
      setError(e instanceof LocationError ? e.message : '現在地を取得できませんでした。');
    } finally {
      setLocating(false);
    }
  };

  const onSave = async () => {
    if (!entry || saving) return;
    const hasContent =
      entry.title.trim() !== '' || entry.body.trim() !== '' || media.length > 0;
    if (!hasContent) {
      setError('タイトル・本文・画像のいずれかを入力してください。');
      return;
    }
    setSaving(true);
    try {
      const toSave: Entry = {
        ...entry,
        schemaVersion: SCHEMA_VERSION,
        media: media.map((m) => m.ref),
      };
      const saved = await saveEntry(toSave);
      await enqueueMediaUploads(saved.id, saved.monthKey);
      clearLocalDraft();
      savedRef.current = true;
      setDirty(false);
      navigate(`/entry/${saved.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました。');
      setSaving(false);
    }
  };

  const onCancel = async () => {
    if (dirty && !confirm('編集内容を破棄します。よろしいですか？')) return;
    // 未保存の新規記事に紐づくメディアは残さない
    if (isNew && entry && !savedRef.current) await discardMediaForEntry(entry.id);
    clearLocalDraft();
    setDirty(false);
    if (id) navigate(`/entry/${id}`);
    else navigate(-1);
  };

  const onDelete = async () => {
    if (!id || !entry) return;
    if (!confirm('この記録を削除します。元に戻せません。')) return;
    const dateKey = entry.date.slice(0, 10);
    await deleteEntry(id);
    clearLocalDraft();
    setDirty(false);
    navigate(`/day/${dateKey}`, { replace: true });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        void onSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (drawing) {
    return (
      <DrawingCanvas
        initial={drawing.doc}
        onDone={onDrawingDone}
        onCancel={() => setDrawing(null)}
      />
    );
  }

  if (!entry) {
    return (
      <div className="view">
        <div className="view-body empty">
          <p>{isNew ? '準備中…' : '記録が見つかりません'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view">
      <header className="view-head">
        <button className="nav-btn" onClick={onCancel} aria-label="やめる">
          ‹
        </button>
        <div className="view-title">
          <h1>{isNew ? '新しい記録' : '記録の編集'}</h1>
        </div>
      </header>

      <div className="view-body form">
        {error && <p className="form-error">{error}</p>}

        <label className="field">
          <span className="field-label">日時</span>
          <div className="field-row">
            <input
              type="datetime-local"
              value={toDatetimeLocalValue(entry.date)}
              onChange={(e) => patch({ date: fromDatetimeLocalValue(e.target.value) })}
            />
            <button type="button" className="ghost-btn" onClick={() => patch({ date: toIsoLocal(new Date()) })}>
              今
            </button>
          </div>
        </label>

        <label className="field">
          <span className="field-label">分類</span>
          <select
            value={entry.projectId ? `p:${entry.projectId}` : entry.categoryId ? `c:${entry.categoryId}` : ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith('p:')) patch({ projectId: v.slice(2), categoryId: null });
              else if (v.startsWith('c:')) patch({ categoryId: v.slice(2), projectId: null });
              else patch({ categoryId: null, projectId: null });
            }}
          >
            <option value="">未設定</option>
            {listProjects(projectsDoc).length > 0 && (
              <optgroup label="プロジェクト">
                {listProjects(projectsDoc).map((p) => (
                  <option key={p.id} value={`p:${p.id}`}>
                    ◆ {p.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="カテゴリ">
              {categories.map((c) => (
                <option key={c.id} value={`c:${c.id}`}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          </select>
          <span className="field-hint">
            プロジェクトを選ぶと、カテゴリの代わりにプロジェクト名が表示されます
          </span>
        </label>

        <label className="field">
          <span className="field-label">タイトル</span>
          <input
            type="text"
            maxLength={200}
            value={entry.title}
            placeholder="タイトル"
            onChange={(e) => patch({ title: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field-label">本文</span>
          <textarea
            rows={12}
            maxLength={50000}
            value={entry.body}
            placeholder="今日のできごと"
            onChange={(e) => patch({ body: e.target.value })}
          />
          <span className="field-hint">{entry.body.length.toLocaleString()} 文字</span>
        </label>

        <div className="field">
          <span className="field-label">画像</span>
          {media.length > 0 && (
            <ul className="media-list">
              {media.map((m, i) => (
                <li key={m.ref.id} className="media-item">
                  {m.url ? (
                    <img
                      src={m.url}
                      className={`media-${m.ref.type}`}
                      alt={m.ref.type === 'drawing' ? '手書き' : '写真'}
                    />
                  ) : (
                    <div className="media-missing media-thumb" title="この端末にまだ取得していません">
                      <span>☁︎</span>
                    </div>
                  )}
                  <div className="media-meta">
                    <span>{m.ref.type === 'drawing' ? '✏️ 手書き' : '📷 写真'}</span>
                    <span className="muted">
                      {m.ref.width}×{m.ref.height} / {Math.round(m.ref.bytes / 1024)}KB
                      {!m.url && ' / 未取得'}
                    </span>
                  </div>
                  <div className="media-actions">
                    <button className="icon-btn" onClick={() => moveMedia(i, -1)} disabled={i === 0} aria-label="前へ">
                      ↑
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => moveMedia(i, 1)}
                      disabled={i === media.length - 1}
                      aria-label="後ろへ"
                    >
                      ↓
                    </button>
                    {m.ref.type === 'drawing' && (
                      <button
                        className="icon-btn"
                        onClick={() =>
                          m.strokes
                            ? setDrawing({ mediaId: m.ref.id, doc: m.strokes })
                            : alert(
                                navigator.onLine
                                  ? 'この手書きには再編集用のデータがありません（画像のみが保存されています）。'
                                  : 'オフラインのため再編集用のデータを取得できません。通信できる状態で開き直してください。',
                              )
                        }
                        aria-label="編集"
                      >
                        ✎
                      </button>
                    )}
                    <button className="icon-btn danger" onClick={() => removeMedia(m.ref.id)} aria-label="削除">
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div
            className={`drop-zone${dropActive ? ' is-active' : ''}`}
            onDragOver={(e) => {
              if (bp === 'desktop') {
                e.preventDefault();
                setDropActive(true);
              }
            }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDropActive(false);
              const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
              void addPhotos(files);
            }}
          >
            <div className="btn-row">
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setDrawing({ mediaId: null, doc: null })}
                disabled={importing}
              >
                ✏️ 手書きを追加
              </button>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => fileRef.current?.click()}
                disabled={importing}
              >
                🖼 写真を選ぶ
              </button>
              {bp !== 'desktop' && (
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => cameraRef.current?.click()}
                  disabled={importing}
                >
                  📷 撮影する
                </button>
              )}
            </div>
            {importing && <p className="muted">取り込み中…</p>}
            {bp === 'desktop' && !importing && (
              <p className="muted">ここにドラッグ＆ドロップ、または画像をコピーして貼り付けできます。</p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={pickFiles}
            />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={pickFiles}
            />
          </div>
        </div>

        <div className="field">
          <span className="field-label">位置情報</span>
          {entry.location ? (
            <div className="loc-row">
              <a href={mapUrl(entry.location)} target="_blank" rel="noreferrer" className="detail-loc">
                📍 {formatLatLng(entry.location)}
              </a>
              <span className="badge badge-plain">
                {entry.location.source === 'exif' ? 'EXIF' : '端末'}
              </span>
              <button type="button" className="icon-btn danger" onClick={() => patch({ location: null })}>
                ✕
              </button>
            </div>
          ) : geolocationAvailable() ? (
            <div>
              <button type="button" className="ghost-btn" onClick={captureLocation} disabled={locating}>
                {locating ? '取得中…' : '📍 現在地を取得'}
              </button>
              <p className="muted">
                写真にEXIFの位置情報があればそちらを優先します。押したときだけ取得します。
              </p>
            </div>
          ) : (
            <p className="muted">この環境では位置情報を取得できません（HTTPSが必要です）。</p>
          )}
        </div>

      </div>

      {/*
        保存は入力の流れの先にあるべきなので、ヘッダー右上ではなく本文の下に置く。
        スクロールしても届くよう、編集ペインの下端に貼り付ける。
      */}
      <div className="edit-actions">
        {!isNew && (
          <button type="button" className="danger-btn" onClick={onDelete}>
            削除
          </button>
        )}
        <span className="spacer" />
        <button type="button" className="ghost-btn" onClick={onCancel}>
          キャンセル
        </button>
        <button className="primary-btn save-btn" onClick={onSave} disabled={saving}>
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
}
